import React, { useEffect, useState } from "react";
import { useAppTranslation } from "@/utils/useAppTranslation";
import {
  Alert,
  Button,
  Col,
  Divider,
  Dropdown,
  Flex,
  Form,
  Input,
  Message,
  Row,
  Select,
  Space,
  Spin,
  Upload,
  useMessagedAsync,
} from "@payping/web-components";
import styles from "@/pages/Shop/Invoice/forms/styles.module.scss";
import { Add, CuToman } from "@payping/icons";
import { useAppDispatch, useAppSelector } from "@/utils/redux";
import { Product, ProductConnection } from "@/pages/Shop/Invoice/forms/type";
import CreateItemCard from "@/components/DashInvoice/CreateItemCard";
import ConfigItemModal from "@/pages/Shop/Invoice/forms/ConfigItemModal";
import CreateProductModal from "@/pages/Shop/Invoice/forms/CreateProductModal";
import { unmaskStrToNum } from "@payping/utils/string";
import { UploadChangeParam, UploadFile } from "@payping/web-components/components/Upload/types";
import { uploadApis } from "@payping/api";
import { FormInstance } from "@payping/web-components/components/Form/types";
import { getProductsWithImage } from "@payping/redux/actions";
import {
  ListBoolQueryParamsType,
  ListNumberQueryParamsType,
  ListStringQueryParamsType,
} from "@/pages/Shop/Product/types";
import qs from "query-string";
import { useLocation } from "react-router-dom";
import { SwitchRow } from "@/core/component";
import { MenuProps } from "@payping/web-components/components/menu";
import { useDebounce } from "react-use";

interface Props {
  selectedItems: Product[];
  setSelectedItems: React.Dispatch<React.SetStateAction<Product[]>>;
  calcItem: (item: Product) => Product;
  setCoupon: React.Dispatch<React.SetStateAction<number | undefined>>;
  setSumTotalAmount: React.Dispatch<React.SetStateAction<number>>;
  fileList?: UploadFile[];
  setFileList: React.Dispatch<React.SetStateAction<UploadFile[]>>;
  setTransport: React.Dispatch<React.SetStateAction<number | undefined>>;
  setTotalAmount: React.Dispatch<React.SetStateAction<number | undefined>>;
  setCountProduct: React.Dispatch<React.SetStateAction<number>>;
  showCoupon?: boolean;
  setShowCoupon: React.Dispatch<React.SetStateAction<boolean | undefined>>;
  transportationCost?: boolean;
  setTransportationCost: React.Dispatch<React.SetStateAction<boolean | undefined>>;
  loading: boolean;
  prev: () => void;
  next: () => void;
  form: FormInstance;
}
const ThirdStepInvoice: React.FC<Props> = ({
  selectedItems,
  setSelectedItems,
  calcItem,
  setCoupon,
  setSumTotalAmount,
  setCountProduct,
  fileList,
  setFileList,
  setTransport,
  setTotalAmount,
  showCoupon,
  setShowCoupon,
  transportationCost,
  setTransportationCost,
  loading,
  prev,
  next,
  form,
}: Props) => {
  const { t } = useAppTranslation();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const items = useAppSelector((state) => state.products);
  const [currentItemConfig, setCurrentItemConfig] = useState<Product | null>(null);
  const [createProductVisible, setCreateProductVisible] = useState(false);
  const haveItemsDiscount = selectedItems?.some((item) => item.finalDiscount && item.finalDiscount > 0);
  const { searchText, searchDateFrom, searchDateTo }: ListStringQueryParamsType = qs.parse(location.search);
  const [searchValue, setSearchValue] = useState("");
  const [searchValueDebounce, setSearchValueDebounce] = useState("");
  const { isArchived }: ListBoolQueryParamsType = qs.parse(location.search, { parseBooleans: true });
  const { pageSize = 12, pageNumber = 1 }: ListNumberQueryParamsType = qs.parse(location.search, {
    parseNumbers: true,
  });

  const [] = useDebounce(
    () => {
      setSearchValueDebounce(searchValue);
    },
    200,
    [searchValue],
  );

  useEffect(() => {
    const subTotal = selectedItems?.reduce((acc, item) => acc + (item.price ?? 0), 0) || 0;
    setSumTotalAmount(subTotal);
    setTotalAmount(subTotal);
    const count = selectedItems?.reduce((acc, item) => acc + (parseInt(String(item.quantity), 10) ?? 0), 0) || 0;
    setCountProduct(count);
  }, [JSON.stringify(selectedItems)]);

  const { loading: loadingList } = useMessagedAsync(
    () =>
      dispatch(
        getProductsWithImage({
          limit: pageSize,
          offset: (pageNumber - 1) * pageSize,
          searchDateFrom,
          searchDateTo,
          searchText: searchText ?? searchValueDebounce,
          withArchived: isArchived,
        }),
      ),
    [pageSize, pageNumber, searchText, isArchived, searchDateFrom, searchDateTo, searchValueDebounce],
    { success: false, loading: false },
  );

  const onFinish = () => {
    next();
  };

  const itemButton: MenuProps["items"] = [
    { key: 0, label: "میزان تخفیف", onClick: () => setShowCoupon(true) },
    { key: 1, label: "هزینه حمل و نقل", onClick: () => setTransportationCost(true) },
  ];
  return (
    <Form<ProductConnection>
      canDeactivate
      scrollToFirstError={{ behavior: "smooth" }}
      form={form}
      layout={"vertical"}
      onFinish={onFinish}
      onValuesChange={(changedValues, values) => {
        if ("discountAmount" in changedValues || "discountType" in changedValues || "shipping" in changedValues) {
          const subTotal = selectedItems?.reduce((acc, item) => acc + (item.price ?? 0), 0) || 0;
          let totalDiscountAmount = selectedItems?.reduce((acc, item) => acc + (item.finalDiscount ?? 0), 0) || 0;
          let total: number;
          let discountAmount = unmaskStrToNum(values.discountAmount || "");
          const { discountType } = values;

          if (discountAmount) {
            if (discountAmount < 0) discountAmount = 0;
            if (discountType === 1) {
              totalDiscountAmount = discountAmount;
            }

            if (discountType === 0) {
              if (discountAmount > 100) discountAmount = 100;
              totalDiscountAmount = (subTotal * discountAmount) / 100;
            }
          }

          total = subTotal;
          setTotalAmount(total);
          total -= totalDiscountAmount;
          if (total < 0) total = 0;

          const shipping = parseInt((values.shipping ?? 0).toString(), 10);

          if (shipping) {
            total += shipping;
          }
          setTransport(shipping);

          setCoupon(totalDiscountAmount);
          setTotalAmount(total);
        }
      }}
    >
      <Form.Item>
        <Select
          placeholder={t("invoice.create.base.searchAndSelect")}
          className="tw-w-full"
          value={null}
          onChange={(value: string) => {
            const item = items.list.find((listItem) => listItem.code === value);
            if (item) {
              setSelectedItems([...(selectedItems || []), calcItem({ ...item, quantity: 1 })]);
              if (item.defineAmountByUser) {
                Message.warning({
                  content: t("invoice.create.base.setPrice"),
                  duration: 3,
                });
                setCurrentItemConfig(item);
              }
            }
          }}
          showSearch
          onSearch={(value) => {
            setSearchValue(value);
          }}
          filterOption={(input, option) =>
            (option?.children as unknown as string)?.toLowerCase().indexOf(input.toLowerCase()) >= 0
          }
          dropdownRender={(menu) => {
            if (loading || loadingList) {
              return (
                <div className="tw-flex tw-w-full tw-items-center tw-justify-center tw-p-4">
                  <Spin />
                </div>
              );
            }
            return (
              <div>
                {menu}
                <Divider className={styles.divider} />
                <div onClick={() => setCreateProductVisible(true)}>
                  <a className={styles.anchor}>
                    <Add width={14} />
                    <span className={styles.mr5}>{t("invoice.create.base.addItem")}</span>
                  </a>
                </div>
              </div>
            );
          }}
        >
          {items.list.map((item, index) => (
            <Select.Option
              value={item.code}
              key={index}
              disabled={selectedItems.some((selectedItem) => selectedItem.code === item.code)}
            >
              {item.title ?? ""}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>
      <Form.Item>
        <Flex vertical gap="small" className="tw-w-full sm:tw-w-1/2">
          {selectedItems?.map((item) => (
            <CreateItemCard
              key={item.code}
              item={item}
              onEdit={(_item) => setCurrentItemConfig(_item)}
              onRemove={() => setSelectedItems(selectedItems.filter((_item) => _item.code !== item.code))}
            />
          ))}
        </Flex>
      </Form.Item>
      <Form.Item label={"اضافه کردن هزینه"}>
        <Dropdown placement={"bottomRight"} menu={{ items: itemButton }}>
          <Button icon={<Add width={14} height={14} />}>{"افزودن"}</Button>
        </Dropdown>
      </Form.Item>
      {showCoupon && (
        <Form.Item label={t("invoice.create.base.discount")} shouldUpdate={() => true}>
          {({ getFieldValue }) => (
            <Flex flex={1}>
              <Space.Compact>
                <Form.Item
                  noStyle
                  name="discountAmount"
                  initialValue={0}
                  rules={[
                    getFieldValue("discountType") === 0
                      ? { max: 99, min: 0, transform: (value): number => parseInt(value, 10), type: "number" }
                      : { transform: (value): number => parseInt(value, 10), type: "number" },
                  ]}
                >
                  <Input.Number
                    skeleton={loading}
                    suffix={getFieldValue("discountType") === 0 ? "%" : <CuToman width="20px" height="20px" />}
                    className="tw-w-full"
                    separateThousandth
                    inputMode="numeric"
                    changeOnWheel={false}
                    controls={false}
                  />
                </Form.Item>
                <Form.Item noStyle name="discountType" initialValue={0}>
                  <Select disabled={haveItemsDiscount} skeleton={loading}>
                    <Select.Option value={0}>{t("invoice.create.base.percent")}</Select.Option>
                    <Select.Option value={1}>{t("invoice.create.base.amount")}</Select.Option>
                  </Select>
                </Form.Item>
              </Space.Compact>
              {haveItemsDiscount && (
                <Alert message="" description={t("invoice.create.base.moreDiscount")} type="info" />
              )}
            </Flex>
          )}
        </Form.Item>
      )}
      {transportationCost && (
        <Form.Item label={t("invoice.create.base.transportationCost")} name={"shipping"} initialValue={0}>
          <Input.Number
            skeleton={loading}
            className="tw-w-full"
            suffix={<CuToman width={24} height={24} />}
            separateThousandth
            inputMode="numeric"
            changeOnWheel={false}
            controls={false}
          />
        </Form.Item>
      )}
      <Form.Item label={"افزودن فایل"} name={"attachments"} shouldUpdate={() => true}>
        <Upload
          disabled={loading}
          listType={"picture-card"}
          onChange={(info: UploadChangeParam): void => setFileList(info.fileList)}
          fileList={fileList}
          crop={false}
          showUploadList={{ showPreviewIcon: true, showRemoveIcon: true }}
          allowedExtensions={["jpg", "jpeg", "png", "pdf", "xls", "zip", "rar"]}
          requestFunction={uploadApis.invoiceAttachmentWithFilePost}
          showTooltip
        >
          <div>
            <Add width={"14px"} className={"color--primary"} />
            <div className={"ant-upload-text"}>{t("invoice.form.upload.label")}</div>
          </div>
        </Upload>
      </Form.Item>
      <Form.Item name={"sendAttachmentsAfterSuccessPayment"} valuePropName={"checked"}>
        <SwitchRow label={"نمایش فایل‌ها فقط پس از پرداخت"} loading={loading} />
      </Form.Item>
      <Form.Item>
        <Row gutter={[8, 8]}>
          <Col flex={2}>
            <Button block type={"primary"} htmlType={"submit"}>
              {t("shop.createInvoice.nextStep")}
            </Button>
          </Col>
          <Col flex={1}>
            <Button block onClick={() => prev()}>
              {t("back")}
            </Button>
          </Col>
        </Row>
      </Form.Item>
      {currentItemConfig && (
        <ConfigItemModal
          maxQuantity={items.list.find((_item) => _item.code === currentItemConfig?.code)?.quantity}
          item={currentItemConfig}
          onCancel={() => setCurrentItemConfig(null)}
          onOk={(updatedItem) => {
            setSelectedItems(
              selectedItems?.map((item) => (item.code === updatedItem.code ? calcItem(updatedItem) : item)),
            );
            setCurrentItemConfig(null);
          }}
        />
      )}
      <CreateProductModal
        visible={createProductVisible}
        onCancel={() => setCreateProductVisible(false)}
        onOk={async (val) => {
          const v = await calcItem(val);
          setSelectedItems((prevState) => [...prevState, v]);
          setCreateProductVisible(false);
        }}
      />
    </Form>
  );
};

export default ThirdStepInvoice;
